import { useEffect, useState } from "react";
import { useLessonSettings } from "hooks/redux";
import { Space } from "components/Common/Space";
import { SentenceItem } from "../SentenceItem";
import {
  TExerciseSentence,
  TInputWordsIntoGapsExercise,
  TInputWordsIntoGapsExerciseBlank,
} from "types/app/exercises";
import "./SentenceList.styles.scss";

type TSentenceListProps = {
  /**
   * An array of sentence data to construct an exercise.
   */
  sentences: TExerciseSentence<TInputWordsIntoGapsExerciseBlank>[];
  /**
   * Full exercise object to construct payload for saving progress.
   */
  exercise: TInputWordsIntoGapsExercise;
  /**
   * Function to update the parent exercise component if needed.
   */
  onSaveExercise: (updatedExercise: TInputWordsIntoGapsExercise) => void;
};

export const SentenceList: React.FC<TSentenceListProps> = (props) => {
  const { sentences, exercise, onSaveExercise } = props;

  const [innerSentences, setInnerSentences] =
    useState<TExerciseSentence<TInputWordsIntoGapsExerciseBlank>[]>(sentences);

  const { isExerciseLiveMode, isExercisePreviewMode } = useLessonSettings();

  useEffect(() => {
    if (isExercisePreviewMode) {
      setInnerSentences(
        sentences.map((sentence) => ({
          ...sentence,
          chunks: sentence.chunks.map((chunk) => ({
            ...chunk,
            blank:
              chunk.blank !== null
                ? {
                    ...chunk.blank,
                    currentAnswer: chunk.blank.answers[0] || null,
                    isCorrectAnswer: true,
                  }
                : null,
          })),
        }))
      );
    }
    if (isExerciseLiveMode) {
      setInnerSentences(
        sentences.map((sentence) => ({
          ...sentence,
          chunks: sentence.chunks.map((chunk) => ({
            ...chunk,
            blank:
              chunk.blank !== null
                ? {
                    ...chunk.blank,
                    currentAnswer: chunk.blank.isCorrectAnswer
                      ? chunk.blank.currentAnswer || chunk.blank.answers[0]
                      : null,
                    isEmpty: !chunk.blank.isCorrectAnswer,
                  }
                : null,
          })),
        }))
      );
    }
  }, [isExerciseLiveMode, sentences]);

  const sentenceChangeHandler = (
    updatedSentence: TExerciseSentence<TInputWordsIntoGapsExerciseBlank>,
    sentenceIndex: number
  ) => {
    const updatedSentences = innerSentences.map((sentence, index) =>
      index === sentenceIndex ? updatedSentence : sentence
    );
    setInnerSentences(updatedSentences);

    const hasFinalizedAnswer = updatedSentence.chunks.some((chunk) => {
      const blank = chunk.blank;
      return blank?.isCorrectAnswer || blank?.isAllAttemptsFailed;
    });

    if (hasFinalizedAnswer) {
      const updatedExercise = {
        ...exercise,
        payload: {
          ...exercise.payload,
          data: {
            ...exercise.payload.data,
            sentences: updatedSentences,
          },
        },
      };

      onSaveExercise(updatedExercise);
    }
  };

  return (
    <Space direction="vertical" size="medium" justify="start" fullWidth>
      {innerSentences.map((sentence, index) => (
        <SentenceItem
          key={index}
          sentence={sentence}
          sentenceIndex={index}
          onSentenceChange={sentenceChangeHandler}
        />
      ))}
    </Space>
  );
};
